import { Component, computed, inject, input, OnDestroy, OnInit, signal } from '@angular/core';
import { select, Store } from '@ngrx/store';
import type { Observable, Subscription } from 'rxjs';

import type { Maybe, Nullable, ValueOf } from '@evc/web-components';
import { ThemeService } from '@evc/web-components';
import { TranslatePipe } from '@shared/services/i18n/i18n.module';

import { getDataSearch } from '@app/store/selectors';

type SectionEmptyData = {
  imgSrcBase: string;
  extension: string;
  text: string;
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const sectionCategories = {
  DRIVE: 'drive',
  FOLDER: 'folder',
  SEARCH: 'search',
  TRASH: 'trash',
  NO_ACCESS: 'noAccess',
} as const;
export type SectionCategories = ValueOf<typeof sectionCategories>;

const categoryEmptyMap: Record<SectionCategories, SectionEmptyData> = {
  drive: {
    imgSrcBase: 'assets/images/ill-no-recent-documents',
    extension: '.png',
    text: 'list.empty',
  },
  folder: {
    imgSrcBase: 'assets/images/ill-empty-folder-{theme}',
    extension: '.png',
    text: 'folder.empty',
  },
  search: {
    imgSrcBase: 'assets/images/ill-no-search-results-{theme}',
    extension: '.png',
    text: 'search.empty',
  },
  trash: {
    imgSrcBase: 'assets/images/ill-trash-empty-{theme}',
    extension: '.png',
    text: 'trash.empty',
  },
  noAccess: {
    imgSrcBase: 'assets/images/ill-nothing-selected-gray',
    extension: '.png',
    text: 'labels.no-access',
  },
};

@Component({
  selector: 'drive-no-item',
  templateUrl: './no-item.component.html',
  styleUrls: ['./no-item.component.scss'],
  imports: [
    TranslatePipe,
  ],
})
export class NoItemComponent implements OnInit, OnDestroy {
  sectionCategory = input<Maybe<SectionCategories>>();

  #store: Store = inject(Store);
  #resolvedTheme = inject(ThemeService).resolvedTheme;

  #dataSearch$: Observable<string> = this.#store.pipe(select(getDataSearch));
  readonly categoryEmptyData = signal<Nullable<SectionEmptyData>>(null);

  #subscription?: Subscription;

  imgSrc = computed(() => {
    const state = this.categoryEmptyData();
    if (!state) return null;
    const base = state.imgSrcBase.replace('{theme}', this.#resolvedTheme());

    return `${base}${state.extension}`;
  });

  ngOnInit(): void {
    this.#subscription = this.#dataSearch$.subscribe((dataSearch) => {
      if (dataSearch) {
        this.categoryEmptyData.set(categoryEmptyMap.search);

        return;
      }
      const category = this.sectionCategory();
      if (category && categoryEmptyMap[category]) {
        this.categoryEmptyData.set(categoryEmptyMap[category]);
      } else {
        this.categoryEmptyData.set(null);
      }
    });
  }

  ngOnDestroy(): void {
    this.#subscription?.unsubscribe();
  }
}
